import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { contactsApi, type ApiListResponse } from '@/lib/api'
import { useNotificationStore } from './notifications'

export interface EmailEntry {
  value: string
  primary?: boolean
}

export interface PhoneEntry {
  value: string
  type?: string
}

export interface TimelineItem {
  _id: string
  type: 'activity' | 'note'
  title: string
  description?: string
  body?: string
  createdAt: string
  updatedAt: string
  createdBy: string
  author?: {
    _id: string
    name: string
    email: string
  }
  // Activity-specific fields (when type === 'activity')
  dueAt?: string
  status?: string
  priority?: string
  activityType?: string
  audioAttachments?: Array<{
    _id: string
    mime: string
    originalName: string
    size: number
  }>
  // Note-specific fields (when type === 'note')
  attachments?: Array<{
    _id: string
    originalName: string
    mime: string
    size: number
  }>
}

/**
 * Headline of a contact's AI lead qualification, as returned on LIST rows (`GET /contacts`).
 * The full qualification (reason, conversations, open questions) comes from the call-analysis
 * endpoints. Absent when the contact has not been scored.
 */
export interface ContactLeadQualification {
  status: string // 'qualified' | 'nurture' | 'in_progress' | 'disqualified'
  score?: number
}

/**
 * The contact's most recently updated open deal (not archived, stage not won/lost), on LIST rows.
 * `null` = the contact has none; the field is absent when the API did not look (e.g. all=true).
 */
export interface ContactOpenDeal {
  _id: string
  name: string
  stageId: string | null
  pipelineId: string | null
}

export interface Contact {
  _id: string
  firstName: string
  lastName?: string
  title?: string
  emails: EmailEntry[]
  phones: PhoneEntry[]
  companyId?: string
  contactOwner?: string
  ownerId?: string
  jobTitle?: string
  linkedInUrl?: string
  source?: string
  priority?: 'low' | 'normal' | 'medium' | 'high' | 'urgent'
  tags: string[]
  custom?: Record<string, any>
  isSeen?: boolean // undefined or true = seen, false = unseen (new contact)
  hasDeal?: boolean // Whether the contact has been converted to a deal
  emailOptOut?: boolean // Opted out of outreach email (filter: emailOptOut)
  lastActivityAt?: string // Last touch (activity, note, call); sortable via sortBy=lastActivityAt
  leadQualification?: ContactLeadQualification // List rows only
  openDeal?: ContactOpenDeal | null // List rows only
  orgId: string
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
  // Populated company information when included
  company?: {
    _id: string
    name: string
    domain?: string
    industry?: string
  }
  // Populated owner information when included
  owner?: {
    _id: string
    name: string
    email: string
  }
  // Activities when included
  activities?: {
    _id: string
    type: string
    title: string
    description?: string
    dueAt: string
    status: string
    priority?: string
    createdAt: string
    updatedAt: string
  }[]
  // Notes when included
  notes?: {
    _id: string
    body: string
    createdBy: string
    createdAt: string
    updatedAt: string
    author?: {
      _id: string
      name: string
      email: string
    }
  }[]
  // Deals when included
  deals?: {
    _id: string
    name: string
    description?: string
    value: number
    currency: string
    status: string
    source?: string
    company?: string
    contactName?: string
    contactEmail?: string
    contactPhone?: string
    assignedTo?: string
    companyId?: string
    contactId?: string
    // Stage fields, so the contact page can draw the deal's stage (GET /contacts/:id?includeDeals)
    stageId?: string
    pipelineId?: string
    lastActivityAt?: string
    isArchived?: boolean
    createdAt: string
    updatedAt: string
    assignee?: {
      _id: string
      name: string
      email: string
    }
  }[]
  // For convenience, we'll add computed properties for display
  email?: string // Primary email for backward compatibility
  phone?: string // Primary phone for backward compatibility
}

export interface ContactsState {
  contacts: Contact[]
  loading: boolean
  error: string | null
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  } | null
}

export const useContactsStore = defineStore('contacts', () => {
  const contacts = ref<Contact[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<ContactsState['meta']>(null)
  const campaigns = ref<{ name: string; count: number }[]>([])

  const notifications = useNotificationStore()

  // Computed
  const contactsCount = computed(() => contacts.value.length)

  const contactsByCompany = computed(() => {
    const grouped: Record<string, Contact[]> = {}
    contacts.value.forEach(contact => {
      const companyId = contact.companyId || 'unassigned'
      if (!grouped[companyId]) {
        grouped[companyId] = []
      }
      grouped[companyId].push(contact)
    })
    return grouped
  })

  const contactsByOwner = computed(() => {
    const grouped: Record<string, Contact[]> = {}
    contacts.value.forEach(contact => {
      const ownerId = contact.contactOwner || 'unassigned'
      if (!grouped[ownerId]) {
        grouped[ownerId] = []
      }
      grouped[ownerId].push(contact)
    })
    return grouped
  })

  // Actions
  // Latest request wins: a quick tab / filter / search / page change can leave an older, slower list
  // response arriving last, which must not overwrite the newer list. A replace bumps the sequence;
  // an append (load more) belongs to the current list and is dropped if one replaced it.
  let listSeq = 0
  async function fetchContacts(params?: any, append: boolean = false): Promise<void> {
    const seq = append ? listSeq : ++listSeq
    loading.value = true
    error.value = null

    try {
      // Always include company and owner information when fetching contacts
      const queryParams = {
        ...params,
        includeCompany: true,
        includeOwner: true
      }
      const response: ApiListResponse<Contact> = await contactsApi.list(queryParams)
      if (seq !== listSeq) return

      if (append) {
        // Append new contacts to existing list (for infinite scroll)
        contacts.value = [...contacts.value, ...response.data]
      } else {
        // Replace contacts list
        contacts.value = response.data
      }
      meta.value = response.meta
    } catch (err: any) {
      if (seq !== listSeq) return
      error.value = err.response?.data?.error?.message || 'Failed to fetch contacts'
      notifications.error('Failed to load contacts')
    } finally {
      if (seq === listSeq) loading.value = false
    }
  }

  async function fetchContact(id: string, options?: { includeActivities?: boolean; includeNotes?: boolean; includeDeals?: boolean }): Promise<Contact | null> {
    loading.value = true
    error.value = null

    try {
      const params = {
        includeCompany: true,
        includeOwner: true,
        includeActivities: options?.includeActivities || false,
        includeNotes: options?.includeNotes || false,
        includeDeals: options?.includeDeals || false
      }
      const response = await contactsApi.get(id, params)
      const contact = response.data

      // Update contact in store if it exists
      const index = contacts.value.findIndex(c => c._id === id)
      if (index !== -1) {
        contacts.value[index] = contact
      } else {
        contacts.value.push(contact)
      }

      return contact
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch contact'
      notifications.error('Failed to load contact')
      return null
    } finally {
      loading.value = false
    }
  }

  async function createContact(contactData: Partial<Contact>): Promise<Contact> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.create(contactData)
      const newContact = response.data
      contacts.value.push(newContact)
      notifications.success('Contact created successfully')
      return newContact
    } catch (err: any) {
      error.value = err.response?.data?.message || err.response?.data?.error?.message || 'Failed to create contact'
      throw err // Re-throw so component can handle it
    } finally {
      loading.value = false
    }
  }

  async function updateContact(id: string, contactData: Partial<Contact>): Promise<Contact> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.update(id, contactData)
      const updatedContact = response.data

      const index = contacts.value.findIndex(c => c._id === id)
      if (index !== -1) {
        contacts.value[index] = updatedContact
      }

      notifications.success('Contact updated successfully')
      return updatedContact
    } catch (err: any) {
      error.value = err.response?.data?.message || err.response?.data?.error?.message || 'Failed to update contact'
      throw err // Re-throw so component can handle it
    } finally {
      loading.value = false
    }
  }

  async function deleteContact(id: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await contactsApi.delete(id)
      contacts.value = contacts.value.filter(c => c._id !== id)
      notifications.success('Contact deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete contact'
      notifications.error('Failed to delete contact')
      return false
    } finally {
      loading.value = false
    }
  }

  function getContactById(id: string): Contact | undefined {
    return contacts.value.find(c => c._id === id)
  }

  function getContactsByCompany(companyId: string): Contact[] {
    return contacts.value.filter(c => c.companyId === companyId)
  }

  function getContactFullName(contact: Contact): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
  }

  async function convertToDeal(
    contactId: string,
    convertData: {
      dealTitle?: string
      description?: string
      value?: number
      source?: string
      pipelineId?: string
      stageId?: string
      probability?: number
      expectedCloseDate?: string
    } = {}
  ): Promise<any | null> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.convertToDeal(contactId, convertData)
      notifications.success('Contact successfully converted to deal')
      return response.data
    } catch (err: any) {
      console.error('Error converting contact to deal:', err)
      error.value = err.response?.data?.message || err.message || 'Failed to convert contact to deal'
      notifications.error(error.value || 'Failed to convert contact to deal')
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchContactTimeline(
    contactId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<{ data: TimelineItem[]; meta: any } | null> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.getTimeline(contactId, params)
      return response
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch timeline'
      notifications.error('Failed to load timeline')
      return null
    } finally {
      loading.value = false
    }
  }

  async function bulkUpdateOwner(
    contactIds: string[],
    newOwnerId: string
  ): Promise<{ updated: number; failed: number } | null> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.bulkUpdateOwner(contactIds, newOwnerId)
      const result = response.data || response
      notifications.success(`Successfully updated ${result.updated} contact(s)`)
      // Note: Caller should reload contacts with proper pagination params if needed
      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to update contacts'
      notifications.error(error.value || 'Failed to update contacts')
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchContactCampaigns(): Promise<void> {
    try {
      const response = await contactsApi.getFilterCampaigns()
      const body = response.data || response
      campaigns.value = body.campaigns || []
    } catch (err: any) {
      // Don't surface a blocking error — log and leave campaigns empty so the
      // dropdown still renders (with only the (No campaign) pseudo-option).
      console.error('Failed to fetch contact campaigns:', err)
      campaigns.value = []
    }
  }

  /**
   * Mark a single contact as seen (when user clicks on a contact row)
   */
  async function markAsSeen(contactId: string): Promise<boolean> {
    try {
      await contactsApi.markAsSeen(contactId)

      // Update contact in store using splice to ensure reactivity
      const index = contacts.value.findIndex(c => c._id === contactId)
      if (index !== -1) {
        const updatedContact = { ...contacts.value[index], isSeen: true } as Contact
        contacts.value.splice(index, 1, updatedContact)
      }

      return true
    } catch (err: any) {
      console.error('Failed to mark contact as seen:', err)
      return false
    }
  }

  /**
   * Bulk update isSeen status for multiple contacts
   */
  async function bulkUpdateSeen(
    contactIds: string[],
    isSeen: boolean
  ): Promise<{ updated: number; failed: number } | null> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.bulkUpdateSeen(contactIds, isSeen)
      const result = response.data || response
      notifications.success(`Successfully marked ${result.updated} contact(s) as ${isSeen ? 'seen' : 'unseen'}`)

      // Update contacts in store using splice to ensure reactivity
      contactIds.forEach(id => {
        const index = contacts.value.findIndex(c => c._id === id)
        if (index !== -1) {
          const updatedContact = { ...contacts.value[index], isSeen } as Contact
          contacts.value.splice(index, 1, updatedContact)
        }
      })

      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to update contacts'
      notifications.error(error.value || 'Failed to update contacts')
      return null
    } finally {
      loading.value = false
    }
  }

  function getContactPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent':
        return 'text-danger-text bg-danger-tint'
      case 'high':
        return 'text-warning-text bg-warning-tint'
      case 'medium':
      case 'normal':
        return 'text-ink-2 bg-wash'
      case 'low':
        return 'text-ink-3 bg-wash'
      default:
        return 'text-ink-2 bg-wash'
    }
  }

  function getContactPriorityLabel(priority: string): string {
    return priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Normal'
  }

  function clearContacts(): void {
    contacts.value = []
    meta.value = null
    error.value = null
  }

  // Search contacts without modifying main contacts array
  // Used for typeahead/autocomplete fields
  async function searchContacts(searchQuery: string, pageSize: number = 10): Promise<Contact[]> {
    try {
      const response: ApiListResponse<Contact> = await contactsApi.list({
        search: searchQuery,
        pageSize,
        includeCompany: true
      })
      return response.data
    } catch (err: any) {
      console.error('Failed to search contacts:', err)
      return []
    }
  }

  // Archive methods
  const archivedContacts = ref<any[]>([])
  const archivedMeta = ref<ContactsState['meta']>(null)

  async function fetchArchivedContacts(params?: any): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.listArchived(params)
      archivedContacts.value = response.data
      archivedMeta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch archived contacts'
      notifications.error('Failed to load archived contacts')
    } finally {
      loading.value = false
    }
  }

  async function restoreContact(archiveId: string): Promise<Contact | null> {
    loading.value = true
    error.value = null

    try {
      const response = await contactsApi.restoreArchived(archiveId)
      const restoredContact = response.data
      // Add to contacts list
      contacts.value.push(restoredContact)
      // Remove from archived list
      archivedContacts.value = archivedContacts.value.filter(c => c._id !== archiveId)
      notifications.success('Contact restored successfully')
      return restoredContact
    } catch (err: any) {
      error.value = err.response?.data?.message || err.response?.data?.error?.message || 'Failed to restore contact'
      notifications.error(error.value || 'Failed to restore contact')
      return null
    } finally {
      loading.value = false
    }
  }

  async function permanentlyDeleteArchived(archiveId: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await contactsApi.permanentlyDeleteArchived(archiveId)
      archivedContacts.value = archivedContacts.value.filter(c => c._id !== archiveId)
      notifications.success('Contact permanently deleted')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete archived contact'
      notifications.error('Failed to delete archived contact')
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    campaigns,
    contacts,
    loading,
    error,
    meta,
    archivedContacts,
    archivedMeta,

    // Computed
    contactsCount,
    contactsByCompany,
    contactsByOwner,

    // Actions
    fetchContacts,
    fetchContact,
    fetchContactCampaigns,
    fetchContactTimeline,
    createContact,
    updateContact,
    deleteContact,
    convertToDeal,
    bulkUpdateOwner,
    bulkUpdateSeen,
    markAsSeen,
    getContactById,
    getContactsByCompany,
    getContactFullName,
    getContactPriorityColor,
    getContactPriorityLabel,
    clearContacts,
    // Archive actions
    fetchArchivedContacts,
    restoreContact,
    permanentlyDeleteArchived,
    // Search
    searchContacts,
  }
})
